import { useEffect, useState } from 'react'

/**
 * Loading indicator for an in-flight answer.
 *
 * The previous version was a static "Thinking" on an opacity pulse, which is easy to
 * mistake for a stalled UI — orchestrator answers routinely take 30-120s. This one
 * signals liveness three ways at once: a phrase that changes, a spinner in continuous
 * motion, and (after a few seconds) an elapsed counter, which is the only one that
 * proves time is actually passing rather than a CSS animation looping over a dead
 * request.
 */
const PHRASES = [
  'Curving',
  'Flexing',
  "DHQ'ing",
  'Laughing at Drew Ward',
  'Waiting for Tyler to finish talking',
  'Bobby is a glorified cheerleader',
  'Dreaming of Scotland',
  "Listening to Carlson's Lame Jokes",
  'Wishing Alden was also AI',
  'Rolling Eyes at SDRs',
  'Thanking God for Product Specialists',
  'Crushing Breck on the ice',
  'Wondering what Jake is doing in FL',
  'Outdriving Julia',
  'Drinking Mushroom Powder with Kevin',
  "Measuring David's Height",
  'Josh Scares Me',
]

const PHRASE_MS = 2800

/** A random phrase, never the one already showing.
 *
 *  The first version shuffled once on mount and then walked that order, so a single
 *  wait always played the same sequence. This picks fresh every tick. Excluding the
 *  current phrase matters: a repeat would render as the indicator freezing, which is
 *  the exact impression this component exists to prevent. */
function nextPhrase(current: string): string {
  if (PHRASES.length < 2) return PHRASES[0]
  let pick = current
  while (pick === current) pick = PHRASES[Math.floor(Math.random() * PHRASES.length)]
  return pick
}

interface ThinkingProps {
  /** Live tool progress from the stream, e.g. "Searching transcripts: ...". */
  progress?: string
  /** Answer text streamed so far, shown as it is written. */
  partial?: string
}

export function Thinking({ progress, partial }: ThinkingProps = {}) {
  const [phrase, setPhrase] = useState<string>(
    () => PHRASES[Math.floor(Math.random() * PHRASES.length)],
  )
  const [elapsed, setElapsed] = useState(0)

  useEffect(() => {
    const rotate = setInterval(() => setPhrase((p) => nextPhrase(p)), PHRASE_MS)
    const clock = setInterval(() => setElapsed((s) => s + 1), 1000)
    return () => {
      clearInterval(rotate)
      clearInterval(clock)
    }
  }, [])

  // Once the answer starts arriving, show the answer — a joke phrase over the top of
  // real content reads as clutter. The cursor marks that more is still coming.
  if (partial) {
    return (
      <div className="whitespace-pre-wrap text-marker-text">
        {partial}
        <span className="inline-block w-2 h-4 ml-0.5 align-text-bottom bg-marker-accent animate-pulse" />
      </div>
    )
  }

  return (
    <div className="flex items-center gap-2.5 text-marker-muted text-sm">
      <svg
        className="animate-spin h-4 w-4 shrink-0 text-marker-accent"
        viewBox="0 0 24 24"
        fill="none"
        aria-hidden="true"
      >
        <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" opacity="0.2" />
        <path
          d="M12 2a10 10 0 0 1 10 10"
          stroke="currentColor"
          strokeWidth="3"
          strokeLinecap="round"
        />
      </svg>

      {/* aria-live so screen readers announce progress rather than sitting silent.
          Real tool progress outranks the joke phrase: "Searching transcripts" tells
          the user something true about the wait, which is the whole point. */}
      <span aria-live="polite" className="transition-opacity duration-300">
        {progress || phrase}
        <span className="inline-block w-6 text-left">
          <span className="animate-pulse">…</span>
        </span>
      </span>

      {elapsed >= 4 && (
        <span className="text-xs tabular-nums opacity-60">{elapsed}s</span>
      )}
    </div>
  )
}
